import React, { useState, useMemo, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Paper,
    Chip,
    Stack,
    IconButton,
    Card,
    CardContent,
    Grid,
    Divider,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Avatar,
    alpha
} from '@mui/material';
import {
    Close as CloseIcon,
    Search as SearchIcon,
    Person as PersonIcon,
    TableChart as TableIcon,
    SwapHoriz as MoveIcon,
    RemoveCircle as EliminationIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    EmojiEvents as TrophyIcon,
    PlayArrow as StartIcon,
    MergeType as MergeIcon,
    Star as FinalTableIcon,
    Timeline as TimelineIcon,
    Leaderboard as LeaderboardIcon,
    Groups as GroupsIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { t } from '../../../../i18n';
import {
    IRankedTournament,
    ITournamentProgression,
    ITournamentEvent,
    TournamentEventType,
    ITournamentStartEvent,
    IPlayerMoveEvent,
    IPlayerEliminationEvent,
    ITableBreakEvent,
    ITableCreatedEvent,
    ITableMergeEvent,
    IFinalTableFormedEvent,
    ITournamentEndEvent
} from '../../../../Core/State/ILobbyTournamentRankingsState';

// ============================================================================
// CONSTANTS
// ============================================================================

const EVENT_COLORS: Record<TournamentEventType, string> = {
    tournament_start: '#4caf50',
    player_move: '#2196f3',
    player_elimination: '#f44336',
    table_break: '#ff9800',
    table_created: '#8bc34a',
    table_merge: '#9c27b0',
    final_table_formed: '#ffd700',
    tournament_end: '#e91e63'
};

const EVENT_ICONS: Record<TournamentEventType, React.ReactNode> = {
    tournament_start: <StartIcon />,
    player_move: <MoveIcon />,
    player_elimination: <EliminationIcon />,
    table_break: <RemoveIcon />,
    table_created: <AddIcon />,
    table_merge: <MergeIcon />,
    final_table_formed: <FinalTableIcon />,
    tournament_end: <TrophyIcon />
};

const getEventLabel = (type: TournamentEventType): string => {
    const labels: Record<TournamentEventType, string> = {
        tournament_start: t('Tournament:tableStructure.eventTypes.tournamentStart'),
        player_move: t('Tournament:tableStructure.eventTypes.playerMove'),
        player_elimination: t('Tournament:tableStructure.eventTypes.playerElimination'),
        table_break: t('Tournament:tableStructure.eventTypes.tableBroken'),
        table_created: t('Tournament:tableStructure.eventTypes.tableCreated'),
        table_merge: t('Tournament:tableStructure.eventTypes.tablesMerged'),
        final_table_formed: t('Tournament:tableStructure.eventTypes.finalTable'),
        tournament_end: t('Tournament:tableStructure.eventTypes.tournamentEnd')
    };
    return labels[type] || type;
};

const THIN_SCROLLBAR_STYLES = {
    '&::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(255,255,255,.3)',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'rgba(255,255,255,.5)',
    },
};

// ============================================================================
// INTERFACES
// ============================================================================

interface TableStructureModalProps {
    open: boolean;
    onClose: () => void;
    tournament: IRankedTournament;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

// Grouped event for batched player moves
interface IGroupedMoveEvent {
    type: 'grouped_moves';
    batchId: string;
    timestamp: string;
    playersRemaining: number;
    tablesActive: number;
    moves: IPlayerMoveEvent[];
    reason: string;
    sourceTable?: number;
}

type IDisplayEvent = ITournamentEvent | IGroupedMoveEvent;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Groups events for display - batches player_move events with same batchId into single entries
 * Also sorts consecutive elimination events (within 2 seconds) by position descending
 */
function groupEventsForDisplay(events: ITournamentEvent[]): IDisplayEvent[] {
    const result: IDisplayEvent[] = [];
    const processedBatchIds = new Set<string>();

    for (const event of events) {
        if (event.type === 'player_move') {
            const moveEvent = event as IPlayerMoveEvent;
            const batchId = moveEvent.batchId;

            // If has batchId and not yet processed, group all moves with same batchId
            if (batchId && !processedBatchIds.has(batchId)) {
                processedBatchIds.add(batchId);
                const batchMoves = events.filter(
                    e => e.type === 'player_move' && (e as IPlayerMoveEvent).batchId === batchId
                ) as IPlayerMoveEvent[];

                if (batchMoves.length > 1) {
                    // Create grouped event
                    result.push({
                        type: 'grouped_moves',
                        batchId,
                        timestamp: batchMoves[0].timestamp,
                        playersRemaining: batchMoves[0].playersRemaining,
                        tablesActive: batchMoves[0].tablesActive,
                        moves: batchMoves,
                        reason: batchMoves[0].reason,
                        sourceTable: batchMoves[0].fromTableNumber
                    });
                } else {
                    // Single move, add as-is
                    result.push(moveEvent);
                }
            } else if (!batchId) {
                // No batchId, add as individual event
                result.push(moveEvent);
            }
            // Skip if batchId already processed
        } else {
            // Non-move events pass through
            result.push(event);
        }
    }

    // Sort consecutive elimination events within 2 seconds by position descending
    return sortConsecutiveEliminations(result);
}

/**
 * Sorts consecutive elimination events that occur within 2 seconds of each other
 * by position in descending order (e.g., 6, 5, 4, 3, 2...)
 */
function sortConsecutiveEliminations(events: IDisplayEvent[]): IDisplayEvent[] {
    const THRESHOLD_MS = 2000; // 2 seconds
    const result: IDisplayEvent[] = [];
    let i = 0;

    while (i < events.length) {
        const current = events[i];

        // Check if this is an elimination event
        if (current.type === 'player_elimination') {
            // Collect consecutive eliminations within 2 seconds
            const eliminationGroup: IPlayerEliminationEvent[] = [current as IPlayerEliminationEvent];
            let j = i + 1;

            while (j < events.length) {
                const next = events[j];
                if (next.type !== 'player_elimination') break;

                const lastElimTime = new Date(eliminationGroup[eliminationGroup.length - 1].timestamp).getTime();
                const nextElimTime = new Date(next.timestamp).getTime();

                // Check if within 2 seconds of the previous elimination
                if (Math.abs(nextElimTime - lastElimTime) <= THRESHOLD_MS) {
                    eliminationGroup.push(next as IPlayerEliminationEvent);
                    j++;
                } else {
                    break;
                }
            }

            // Sort the group by position descending (higher position first: 6, 5, 4, 3, 2...)
            if (eliminationGroup.length > 1) {
                eliminationGroup.sort((a, b) => b.position - a.position);
            }

            // Add sorted eliminations to result
            result.push(...eliminationGroup);
            i = j;
        } else {
            result.push(current);
            i++;
        }
    }

    return result;
}

function getProgressionStats(progression: ITournamentProgression) {
    const events = progression.events;
    const eliminations = events.filter(e => e.type === 'player_elimination') as IPlayerEliminationEvent[];
    const moves = events.filter(e => e.type === 'player_move') as IPlayerMoveEvent[];
    const tableBreaks = events.filter(e => e.type === 'table_break');
    const endEvent = events.find(e => e.type === 'tournament_end') as ITournamentEndEvent | undefined;
    const finalTable = events.find(e => e.type === 'final_table_formed') as IFinalTableFormedEvent | undefined;

    return {
        totalPlayers: progression.totalPlayers,
        totalTables: progression.totalTables,
        totalEliminations: eliminations.length,
        totalMoves: moves.length,
        tablesBroken: tableBreaks.length,
        totalHands: endEvent?.totalHands || 0,
        durationMinutes: endEvent?.durationMinutes || 0,
        winner: endEvent ? { username: endEvent.winnerUsername, prize: endEvent.winnerPrize } : null,
        finalTablePlayers: finalTable?.players?.length || 0,
        isComplete: !!endEvent
    };
}

function getPlayerJourney(progression: ITournamentProgression, playerId: string) {
    const journey: Array<{
        timestamp: string;
        event: string;
        tableNumber?: number;
        fromTableNumber?: number;
        toTableNumber?: number;
        seatNumber?: number;
        chips?: number;
        position?: number;
        reason?: string;
        eliminatedBy?: string;
        prize?: number;
    }> = [];

    const startEvent = progression.events.find(e => e.type === 'tournament_start') as ITournamentStartEvent | undefined;
    if (startEvent) {
        for (const table of startEvent.tables) {
            const player = table.players.find(p => p.playerId === playerId);
            if (player) {
                journey.push({
                    timestamp: startEvent.timestamp,
                    event: 'Started',
                    tableNumber: table.tableNumber,
                    seatNumber: player.seatNumber,
                    chips: player.chips
                });
                break;
            }
        }
    }

    progression.events
        .filter(e => e.type === 'player_move' && (e as IPlayerMoveEvent).playerId === playerId)
        .forEach(e => {
            const moveEvent = e as IPlayerMoveEvent;
            journey.push({
                timestamp: moveEvent.timestamp,
                event: 'Moved',
                fromTableNumber: moveEvent.fromTableNumber,
                toTableNumber: moveEvent.toTableNumber,
                seatNumber: moveEvent.toSeatNumber,
                chips: moveEvent.chips,
                reason: moveEvent.reason
            });
        });

    const elimination = progression.events.find(
        e => e.type === 'player_elimination' && (e as IPlayerEliminationEvent).playerId === playerId
    ) as IPlayerEliminationEvent | undefined;

    if (elimination) {
        journey.push({
            timestamp: elimination.timestamp,
            event: 'Eliminated',
            tableNumber: elimination.tableNumber,
            position: elimination.position,
            eliminatedBy: elimination.eliminatedBy?.map(p => p.username).join(', ')
        });
    }

    const endEvent = progression.events.find(e => e.type === 'tournament_end') as ITournamentEndEvent | undefined;
    if (endEvent && endEvent.winnerId === playerId) {
        journey.push({
            timestamp: endEvent.timestamp,
            event: 'Won',
            prize: endEvent.winnerPrize
        });
    }

    return journey;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index} style={{ height: '100%', overflow: 'hidden' }}>
        {value === index && <Box sx={{ height: '100%', pt: 2, overflow: 'hidden' }}>{children}</Box>}
    </div>
);

const EventCard: React.FC<{ event: IDisplayEvent; isSelected: boolean; onClick: () => void }> = ({
    event, isSelected, onClick
}) => {
    const isGroupedMove = event.type === 'grouped_moves';
    const color = isGroupedMove ? EVENT_COLORS['player_move'] : EVENT_COLORS[event.type as TournamentEventType];

    const getEventDescription = () => {
        if (isGroupedMove) {
            const g = event as IGroupedMoveEvent;
            const destinations = [...new Set(g.moves.map(m => m.toTableNumber))];
            return `${g.moves.length} players moved → Table${destinations.length > 1 ? 's' : ''} ${destinations.join(', ')}`;
        }

        switch (event.type) {
            case 'tournament_start': {
                const e = event as ITournamentStartEvent;
                return `${e.playersRemaining} players across ${e.tablesActive} tables`;
            }
            case 'player_move': {
                const e = event as IPlayerMoveEvent;
                return `${e.username}: Table ${e.fromTableNumber} → Table ${e.toTableNumber}`;
            }
            case 'player_elimination': {
                const e = event as IPlayerEliminationEvent;
                return `${e.username} finished #${e.position}${e.eliminatedBy?.length ? ` by ${e.eliminatedBy[0].username}` : ''}`;
            }
            case 'table_break': {
                const e = event as ITableBreakEvent;
                return `Table ${e.tableNumber} closed, ${e.playersMoved} players redistributed`;
            }
            case 'table_created': {
                const e = event as ITableCreatedEvent;
                return `Table ${e.tableNumber} opened (${e.reason})`;
            }
            case 'table_merge': {
                const e = event as ITableMergeEvent;
                return `Table ${e.sourceTableNumber} merged into Table ${e.destinationTableNumber}`;
            }
            case 'final_table_formed': {
                const e = event as IFinalTableFormedEvent;
                return `${e.players.length} players at Table ${e.tableNumber}`;
            }
            case 'tournament_end': {
                const e = event as ITournamentEndEvent;
                return `${e.winnerUsername} wins $${e.winnerPrize}!`;
            }
            default:
                return '';
        }
    };

    const getLabel = () => {
        if (isGroupedMove) {
            const g = event as IGroupedMoveEvent;
            return `Players Moved (${g.reason})`;
        }
        return getEventLabel(event.type as TournamentEventType);
    };

    const getSeqLabel = () => {
        if (isGroupedMove) {
            const g = event as IGroupedMoveEvent;
            return `${g.moves.length}`;
        }
        return `#${(event as ITournamentEvent).seq}`;
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
        >
            <Card
                onClick={onClick}
                sx={{
                    mb: 1,
                    cursor: 'pointer',
                    borderLeft: 3,
                    borderColor: color,
                    bgcolor: isSelected ? alpha(color, 0.15) : '#1a1a1e',
                    transition: 'all 0.2s',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: alpha(color, 0.1), transform: 'translateX(4px)' }
                }}
            >
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ bgcolor: alpha(color, 0.2), color: color, width: 28, height: 28 }}>
                            {isGroupedMove ? <GroupsIcon sx={{ fontSize: '1rem' }} /> : React.cloneElement(EVENT_ICONS[event.type as TournamentEventType] as React.ReactElement, { sx: { fontSize: '1rem' } })}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff', fontSize: '0.75rem' }}>
                                    {getLabel()}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#666', fontSize: '0.6rem' }}>
                                    {getSeqLabel()}
                                </Typography>
                            </Stack>
                            <Typography variant="caption" sx={{ color: '#888', fontSize: '0.65rem' }} noWrap>
                                {getEventDescription()}
                            </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0}>
                            <Typography variant="caption" sx={{ color: '#888', fontSize: '0.6rem' }}>
                                {format(new Date(event.timestamp), 'HH:mm:ss')}
                            </Typography>
                            <Stack direction="row" spacing={0.5}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: '#666', fontSize: '0.6rem' }}>
                                    <PersonIcon sx={{ fontSize: '0.7rem' }} />
                                    {event.playersRemaining}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: '#666', fontSize: '0.6rem' }}>
                                    <TableIcon sx={{ fontSize: '0.7rem' }} />
                                    {event.tablesActive}
                                </Box>
                            </Stack>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        </motion.div>
    );
};

const EventDetail: React.FC<{ event: IDisplayEvent | null }> = ({ event }) => {
    if (!event) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography sx={{ color: '#666' }}>{t('Tournament:tableStructure.selectEvent')}</Typography>
            </Box>
        );
    }

    const isGroupedMove = event.type === 'grouped_moves';
    const color = isGroupedMove ? EVENT_COLORS['player_move'] : EVENT_COLORS[event.type as TournamentEventType];

    const renderContent = () => {
        switch (event.type) {
            case 'tournament_start': {
                const e = event as ITournamentStartEvent;
                return (
                    <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ color: '#fff' }}>{t('Tournament:tableStructure.initialAssignments')}</Typography>
                        <Grid container spacing={1}>
                            {e.tables.map(table => (
                                <Grid item xs={12} sm={6} key={table.tableId}>
                                    <Paper sx={{ p: 1, bgcolor: '#232329', border: '1px solid #333' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#4fd1c5' }}>
                                            {t('Tournament:tables.table')} {table.tableNumber}
                                        </Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            {table.players.map(p => (
                                                <Typography key={p.playerId} variant="caption" display="block" sx={{ color: '#aaa', fontSize: '0.65rem' }}>
                                                    {t('Tournament:tables.seat')} {p.seatNumber}: {p.username} ({p.chips.toLocaleString()})
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                );
            }
            case 'player_move': {
                const e = event as IPlayerMoveEvent;
                return (
                    <Stack spacing={2}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Avatar sx={{ bgcolor: alpha(color, 0.2), color: color, width: 48, height: 48, mx: 'auto', mb: 1 }}>
                                <PersonIcon />
                            </Avatar>
                            <Typography variant="h6" sx={{ color: '#fff' }}>{e.username}</Typography>
                        </Box>
                        <Divider sx={{ borderColor: '#333' }} />
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#f44336', 0.1), border: '1px solid #f4433640' }}>
                                <Typography variant="caption" sx={{ color: '#888' }}>{t('Tournament:tableStructure.from')}</Typography>
                                <Typography variant="h5" sx={{ color: '#f44336' }}>{t('Tournament:tables.table')} {e.fromTableNumber}</Typography>
                            </Paper>
                            <MoveIcon sx={{ fontSize: 32, color: '#4fd1c5' }} />
                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#4caf50', 0.1), border: '1px solid #4caf5040' }}>
                                <Typography variant="caption" sx={{ color: '#888' }}>{t('Tournament:tableStructure.to')}</Typography>
                                <Typography variant="h5" sx={{ color: '#4caf50' }}>{t('Tournament:tables.table')} {e.toTableNumber}</Typography>
                                <Typography variant="caption" sx={{ color: '#aaa' }}>{t('Tournament:tables.seat')} {e.toSeatNumber}</Typography>
                            </Paper>
                        </Stack>
                        <Divider sx={{ borderColor: '#333' }} />
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: '#888' }}>{t('Tournament:players.chips')}</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#fff' }}>{e.chips.toLocaleString()}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: '#888' }}>{t('Tournament:tableStructure.reason')}</Typography>
                                <Chip label={e.reason} size="small" sx={{ bgcolor: alpha('#4fd1c5', 0.2), color: '#4fd1c5' }} />
                            </Grid>
                        </Grid>
                    </Stack>
                );
            }
            case 'player_elimination': {
                const e = event as IPlayerEliminationEvent;
                return (
                    <Stack spacing={2}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Avatar sx={{ bgcolor: color, width: 48, height: 48, mx: 'auto', mb: 1 }}>
                                <EliminationIcon />
                            </Avatar>
                            <Typography variant="h6" sx={{ color: '#fff' }}>{e.username}</Typography>
                            <Chip
                                label={`Finished #${e.position}`}
                                sx={{ mt: 1, fontWeight: 600, bgcolor: alpha('#f44336', 0.2), color: '#f44336' }}
                            />
                        </Box>
                        <Divider sx={{ borderColor: '#333' }} />
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: '#888' }}>{t('Tournament:tables.table')}</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#fff' }}>{t('Tournament:tables.table')} {e.tableNumber}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: '#888' }}>{t('Tournament:tableStructure.chipsLost')}</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#fff' }}>{e.chipsLost.toLocaleString()}</Typography>
                            </Grid>
                        </Grid>
                        {e.eliminatedBy && e.eliminatedBy.length > 0 && (
                            <Box>
                                <Typography variant="caption" sx={{ color: '#888' }}>{t('Tournament:tableStructure.eliminatedBy')}</Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                    {e.eliminatedBy.map(p => (
                                        <Chip key={p.playerId} label={p.username} size="small" sx={{ bgcolor: '#232329', color: '#aaa' }} />
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                );
            }
            case 'final_table_formed': {
                const e = event as IFinalTableFormedEvent;
                return (
                    <Box>
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                            <Avatar sx={{ bgcolor: color, width: 56, height: 56, mx: 'auto', mb: 1 }}>
                                <FinalTableIcon />
                            </Avatar>
                            <Typography variant="h6" sx={{ color: '#fff' }}>{t('Tournament:tableStructure.finalTableFormed')}</Typography>
                            <Typography variant="caption" sx={{ color: '#888' }}>
                                {t('Tournament:tables.table')} {e.tableNumber} • {e.players.length} {(t('Tournament:info.players') as string).toLowerCase()}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 2, borderColor: '#333' }} />
                        <Typography variant="subtitle2" gutterBottom sx={{ color: '#4fd1c5' }}>{t('Tournament:tableStructure.chipCounts')}</Typography>
                        {[...e.players].sort((a, b) => b.chips - a.chips).map((p, idx) => (
                            <Stack key={p.playerId} direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                <Typography variant="caption" sx={{ width: 20, fontWeight: 600, color: '#888' }}>
                                    {idx + 1}.
                                </Typography>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" sx={{ color: '#fff' }}>{p.username}</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#4fd1c5' }}>
                                    {p.chips.toLocaleString()}
                                </Typography>
                            </Stack>
                        ))}
                    </Box>
                );
            }
            case 'tournament_end': {
                const e = event as ITournamentEndEvent;
                return (
                    <Stack spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: '#ffd700', width: 64, height: 64 }}>
                            <TrophyIcon sx={{ fontSize: 40, color: '#333' }} />
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
                            {e.winnerUsername} Wins!
                        </Typography>
                        <Chip
                            label={`$${e.winnerPrize.toLocaleString()}`}
                            sx={{ fontSize: '1.2rem', height: 36, fontWeight: 700, bgcolor: alpha('#4caf50', 0.2), color: '#4caf50' }}
                        />
                        <Divider sx={{ width: '100%', borderColor: '#333' }} />
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#888' }}>{t('Tournament:tableStructure.duration')}</Typography>
                            <Typography variant="h6" sx={{ color: '#fff' }}>{e.durationMinutes} min</Typography>
                        </Box>
                    </Stack>
                );
            }
            case 'grouped_moves': {
                const g = event as IGroupedMoveEvent;
                // Group moves by destination table
                const byDestination = g.moves.reduce((acc, m) => {
                    const key = m.toTableNumber;
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(m);
                    return acc;
                }, {} as Record<number, IPlayerMoveEvent[]>);

                return (
                    <Box>
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                            <Avatar sx={{ bgcolor: color, width: 48, height: 48, mx: 'auto', mb: 1 }}>
                                <GroupsIcon />
                            </Avatar>
                            <Typography variant="h6" sx={{ color: '#fff' }}>{g.moves.length} {(t('Tournament:info.players') as string)} {t('Tournament:tableStructure.moved')}</Typography>
                            <Chip label={g.reason} size="small" sx={{ mt: 1, bgcolor: alpha('#4fd1c5', 0.2), color: '#4fd1c5' }} />
                        </Box>
                        <Divider sx={{ my: 2, borderColor: '#333' }} />
                        {Object.entries(byDestination).map(([tableNum, moves]) => (
                            <Box key={tableNum} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, color: '#4fd1c5' }}>
                                    <MoveIcon fontSize="small" />
                                    → {t('Tournament:tables.table')} {tableNum}
                                </Typography>
                                <Paper sx={{ p: 1, bgcolor: '#232329', border: '1px solid #333' }}>
                                    {moves.map(m => (
                                        <Stack key={m.playerId} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
                                            <Typography variant="caption" sx={{ minWidth: 80, color: '#fff' }}>
                                                {m.username}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#888' }}>
                                                {t('Tournament:tableStructure.fromTable')} {t('Tournament:tables.table')} {m.fromTableNumber}
                                            </Typography>
                                            <Box sx={{ flexGrow: 1 }} />
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#4fd1c5' }}>
                                                {m.chips.toLocaleString()}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Paper>
                            </Box>
                        ))}
                    </Box>
                );
            }
            default:
                return (
                    <Box>
                        <Typography variant="body2" sx={{ color: '#888' }}>
                            {t('Tournament:info.players')}: {event.playersRemaining} | {t('Tournament:tables.table')}: {event.tablesActive}
                        </Typography>
                    </Box>
                );
        }
    };

    const getHeaderIcon = () => {
        if (isGroupedMove) return <GroupsIcon fontSize="small" />;
        return EVENT_ICONS[event.type as TournamentEventType];
    };

    const getHeaderLabel = () => {
        if (isGroupedMove) {
            const g = event as IGroupedMoveEvent;
            return `Players Moved (${g.moves.length})`;
        }
        return getEventLabel(event.type as TournamentEventType);
    };

    return (
        <Paper sx={{ p: 2, height: '100%', overflow: 'auto', bgcolor: '#1a1a1e', border: '1px solid #333', ...THIN_SCROLLBAR_STYLES }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(color, 0.2), color: color, width: 28, height: 28 }}>
                    {getHeaderIcon()}
                </Avatar>
                <Typography variant="h6" sx={{ color: '#fff' }}>{getHeaderLabel()}</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="caption" sx={{ color: '#888' }}>
                    {format(new Date(event.timestamp), 'HH:mm:ss')}
                </Typography>
            </Stack>
            {renderContent()}
        </Paper>
    );
};

const EliminationFeed: React.FC<{ progression: ITournamentProgression }> = ({ progression }) => {
    const eliminations = useMemo(() => {
        return (progression.events
            .filter(e => e.type === 'player_elimination') as IPlayerEliminationEvent[])
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    }, [progression]);

    return (
        <Box sx={{ height: '100%', overflow: 'auto', ...THIN_SCROLLBAR_STYLES }}>
            {eliminations.map((e, idx) => (
                <motion.div
                    key={e.seq}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{
                            p: 1,
                            borderBottom: '1px solid #333',
                            bgcolor: e.position <= 3 ? alpha('#ffd700', 0.08) : 'transparent'
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                width: 36,
                                fontWeight: 700,
                                color: e.position === 1 ? '#ffd700' : e.position === 2 ? '#c0c0c0' : e.position === 3 ? '#cd7f32' : '#fff'
                            }}
                        >
                            #{e.position}
                        </Typography>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: '#333', color: '#4fd1c5', fontSize: '0.8rem' }}>
                            {e.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#fff' }}>{e.username}</Typography>
                            {e.eliminatedBy && e.eliminatedBy.length > 0 && (
                                <Typography variant="caption" sx={{ color: '#888' }}>
                                    by {e.eliminatedBy[0].username}
                                </Typography>
                            )}
                        </Box>
                        <Stack alignItems="flex-end" spacing={0}>
                            <Typography variant="caption" sx={{ color: '#888' }}>
                                {format(new Date(e.timestamp), 'HH:mm:ss')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.65rem' }}>
                                {t('Tournament:tables.table')} {e.tableNumber}
                            </Typography>
                        </Stack>
                    </Stack>
                </motion.div>
            ))}
        </Box>
    );
};

const PlayerSearch: React.FC<{
    progression: ITournamentProgression;
    searchTerm: string;
    onSearchChange: (term: string) => void;
    selectedPlayerId: string | null;
    onPlayerSelect: (playerId: string) => void;
}> = ({ progression, searchTerm, onSearchChange, selectedPlayerId, onPlayerSelect }) => {
    const players = useMemo(() => {
        const startEvent = progression.events.find(e => e.type === 'tournament_start') as ITournamentStartEvent | undefined;
        if (!startEvent) return [];

        const allPlayers: Array<{ playerId: string; username: string }> = [];
        startEvent.tables.forEach(table => {
            table.players.forEach(p => {
                allPlayers.push({ playerId: p.playerId, username: p.username });
            });
        });

        if (!searchTerm) return allPlayers;
        return allPlayers.filter(p => p.username.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [progression, searchTerm]);

    const selectedJourney = useMemo(() => {
        if (!selectedPlayerId) return null;
        return getPlayerJourney(progression, selectedPlayerId);
    }, [progression, selectedPlayerId]);

    const selectedPlayer = players.find(p => p.playerId === selectedPlayerId);

    return (
        <Grid container spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
            <Grid item xs={12} md={4} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('Tournament:tableStructure.searchPlayer')}
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: '#888' }} />
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        mb: 1,
                        flexShrink: 0,
                        '& .MuiOutlinedInput-root': {
                            bgcolor: '#232329',
                            color: '#fff',
                            '& fieldset': { borderColor: '#333' },
                            '&:hover fieldset': { borderColor: '#4fd1c5' },
                            '&.Mui-focused fieldset': { borderColor: '#4fd1c5' }
                        },
                        '& .MuiInputBase-input::placeholder': { color: '#666' }
                    }}
                />
                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, ...THIN_SCROLLBAR_STYLES }}>
                    {players.map(p => (
                        <Box
                            key={p.playerId}
                            onClick={() => onPlayerSelect(p.playerId)}
                            sx={{
                                p: 1,
                                cursor: 'pointer',
                                bgcolor: selectedPlayerId === p.playerId ? alpha('#4fd1c5', 0.2) : 'transparent',
                                borderRadius: 1,
                                '&:hover': { bgcolor: selectedPlayerId === p.playerId ? alpha('#4fd1c5', 0.2) : '#232329' }
                            }}
                        >
                            <Typography variant="body2" sx={{ color: selectedPlayerId === p.playerId ? '#4fd1c5' : '#fff' }}>{p.username}</Typography>
                        </Box>
                    ))}
                </Box>
            </Grid>
            <Grid item xs={12} md={8} sx={{ height: '100%' }}>
                {selectedJourney && selectedPlayer ? (
                    <Paper sx={{ p: 2, height: '100%', overflow: 'auto', bgcolor: '#1a1a1e', border: '1px solid #333', ...THIN_SCROLLBAR_STYLES }}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>{selectedPlayer.username}'s Journey</Typography>
                        <Divider sx={{ mb: 2, borderColor: '#333' }} />
                        {selectedJourney.map((step, idx) => (
                            <Stack key={idx} direction="row" spacing={2} sx={{ mb: 2 }}>
                                <Box sx={{ position: 'relative' }}>
                                    <Avatar
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            bgcolor: step.event === 'Won' ? '#ffd700'
                                                : step.event === 'Eliminated' ? '#f44336'
                                                : step.event === 'Moved' ? '#2196f3'
                                                : '#4caf50'
                                        }}
                                    >
                                        {step.event === 'Won' ? <TrophyIcon sx={{ fontSize: 18 }} />
                                            : step.event === 'Eliminated' ? <EliminationIcon sx={{ fontSize: 18 }} />
                                            : step.event === 'Moved' ? <MoveIcon sx={{ fontSize: 18 }} />
                                            : <StartIcon sx={{ fontSize: 18 }} />
                                        }
                                    </Avatar>
                                    {idx < selectedJourney.length - 1 && (
                                        <Box sx={{ position: 'absolute', left: 15, top: 36, width: 2, height: 24, bgcolor: '#333' }} />
                                    )}
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#fff' }}>{step.event}</Typography>
                                    <Typography variant="caption" sx={{ color: '#888' }} display="block">
                                        {format(new Date(step.timestamp), 'HH:mm:ss')}
                                    </Typography>
                                    {step.event === 'Moved' && step.fromTableNumber !== undefined && step.toTableNumber !== undefined ? (
                                        <Typography variant="caption" sx={{ color: '#aaa' }}>
                                            {t('Tournament:tables.table')} {step.fromTableNumber} → {t('Tournament:tables.table')} {step.toTableNumber}{step.seatNumber ? `, ${t('Tournament:tables.seat')} ${step.seatNumber}` : ''}
                                        </Typography>
                                    ) : step.tableNumber !== undefined && (
                                        <Typography variant="caption" sx={{ color: '#aaa' }}>
                                            {t('Tournament:tables.table')} {step.tableNumber}{step.seatNumber ? `, ${t('Tournament:tables.seat')} ${step.seatNumber}` : ''}
                                        </Typography>
                                    )}
                                    {step.chips !== undefined && (
                                        <Typography variant="caption" display="block" sx={{ color: '#4fd1c5' }}>
                                            {t('Tournament:players.chips')}: {step.chips.toLocaleString()}
                                        </Typography>
                                    )}
                                    {step.position && (
                                        <Chip label={`#${step.position}`} size="small" sx={{ mt: 0.5, bgcolor: alpha('#f44336', 0.2), color: '#f44336' }} />
                                    )}
                                    {step.prize && (
                                        <Chip label={`$${step.prize}`} size="small" sx={{ mt: 0.5, bgcolor: alpha('#4caf50', 0.2), color: '#4caf50' }} />
                                    )}
                                    {step.eliminatedBy && (
                                        <Typography variant="caption" display="block" sx={{ color: '#888' }}>
                                            Eliminated by {step.eliminatedBy}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        ))}
                    </Paper>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography sx={{ color: '#666' }}>{t('Tournament:tableStructure.selectPlayer')}</Typography>
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const TableStructureModal: React.FC<TableStructureModalProps> = ({ open, onClose, tournament }) => {
    const [tabValue, setTabValue] = useState(0);
    const [selectedEventIndex, setSelectedEventIndex] = useState<number | null>(null);
    const [playerSearchTerm, setPlayerSearchTerm] = useState('');
    const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);

    // tableStructureHistory contains the event-based progression structure (null if not started)
    const progression = tournament.tableStructureHistory;
    const hasProgression = progression && progression.events && progression.events.length > 0;

    // Group player moves with same batchId into single timeline entries
    const displayEvents = useMemo(() => {
        if (!hasProgression || !progression) return [];
        return groupEventsForDisplay(progression.events);
    }, [hasProgression, progression]);

    const stats = useMemo(() => {
        if (!hasProgression || !progression) return null;
        return getProgressionStats(progression);
    }, [hasProgression, progression]);

    const selectedEvent = useMemo((): IDisplayEvent | null => {
        if (!hasProgression || selectedEventIndex === null || selectedEventIndex >= displayEvents.length) return null;
        return displayEvents[selectedEventIndex];
    }, [hasProgression, displayEvents, selectedEventIndex]);

    const handleEventClick = useCallback((index: number) => {
        setSelectedEventIndex(prev => prev === index ? null : index);
    }, []);

    // No progression data - show empty state
    if (!hasProgression) {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { bgcolor: '#18181c', backgroundImage: 'none' } }}
            >
                <DialogTitle sx={{ bgcolor: '#1a1a1e', borderBottom: '1px solid #2e6f6b' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" sx={{ color: '#fff' }}>
                            Tournament Progression - {tournament.tournamentName}
                        </Typography>
                        <IconButton onClick={onClose} sx={{ color: '#888' }}><CloseIcon /></IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ bgcolor: '#18181c' }}>
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <TimelineIcon sx={{ fontSize: 64, color: '#444', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#888' }}>
                            No progression data available
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            Tournament progression will be available once the tournament starts.
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{ sx: { bgcolor: '#18181c', backgroundImage: 'none' } }}
        >
            <DialogTitle sx={{ py: 1.5, px: 2, bgcolor: '#1a1a1e', borderBottom: '1px solid #2e6f6b' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    {/* Title */}
                    <Typography variant="subtitle1" noWrap sx={{ color: '#fff', fontWeight: 600, minWidth: 'fit-content' }}>
                        {tournament.tournamentName}
                    </Typography>

                    {/* Info text */}
                    <Typography variant="body2" sx={{ color: '#888', whiteSpace: 'nowrap' }}>
                        • {progression.events.length} events{stats?.isComplete && ` • ${stats.durationMinutes} min`}
                    </Typography>

                    {/* Spacer */}
                    <Box sx={{ flex: 1 }} />

                    {/* Stat badges */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: alpha('#4caf50', 0.15), px: 1.5, py: 0.5, borderRadius: 1, border: '1px solid', borderColor: alpha('#4caf50', 0.3) }}>
                            <GroupsIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                            <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>{stats?.totalPlayers || 0}</Typography>
                            <Typography variant="caption" sx={{ color: '#888' }}>Players</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: alpha('#f44336', 0.15), px: 1.5, py: 0.5, borderRadius: 1, border: '1px solid', borderColor: alpha('#f44336', 0.3) }}>
                            <EliminationIcon sx={{ fontSize: 16, color: '#f44336' }} />
                            <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 600 }}>
                                {stats?.isComplete ? (stats?.totalPlayers || 1) - 1 : (stats?.totalEliminations || 0)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#888' }}>{t('Tournament:tableStructure.eliminations')}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: alpha('#2196f3', 0.15), px: 1.5, py: 0.5, borderRadius: 1, border: '1px solid', borderColor: alpha('#2196f3', 0.3) }}>
                            <TableIcon sx={{ fontSize: 16, color: '#2196f3' }} />
                            <Typography variant="body2" sx={{ color: '#2196f3', fontWeight: 600 }}>{stats?.totalTables || 0}</Typography>
                            <Typography variant="caption" sx={{ color: '#888' }}>{t('Tournament:tableStructure.initialTables')}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: alpha('#ff9800', 0.15), px: 1.5, py: 0.5, borderRadius: 1, border: '1px solid', borderColor: alpha('#ff9800', 0.3) }}>
                            <MoveIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                            <Typography variant="body2" sx={{ color: '#ff9800', fontWeight: 600 }}>{stats?.totalMoves || 0}</Typography>
                            <Typography variant="caption" sx={{ color: '#888' }}>Moves</Typography>
                        </Box>
                        {stats?.winner && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: alpha('#ffd700', 0.15), px: 1.5, py: 0.5, borderRadius: 1, border: '1px solid', borderColor: alpha('#ffd700', 0.3) }}>
                                <TrophyIcon sx={{ fontSize: 16, color: '#ffd700' }} />
                                <Typography variant="body2" sx={{ color: '#ffd700', fontWeight: 600 }}>{stats.winner.username}</Typography>
                                <Typography variant="caption" sx={{ color: '#888' }}>{t('Tournament:rankings.winner')}</Typography>
                            </Box>
                        )}
                    </Stack>

                    <IconButton onClick={onClose} size="small" sx={{ color: '#888', ml: 1 }}><CloseIcon /></IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ height: '80vh', display: 'flex', flexDirection: 'column', p: 0, bgcolor: '#18181c' }}>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: '#333', px: 2, bgcolor: '#1a1a1e' }}>
                    <Tabs
                        value={tabValue}
                        onChange={(_, v) => setTabValue(v)}
                        sx={{
                            '& .MuiTab-root': { color: '#888', minHeight: 48 },
                            '& .Mui-selected': { color: '#4fd1c5' },
                            '& .MuiTabs-indicator': { bgcolor: '#4fd1c5' }
                        }}
                    >
                        <Tab icon={<TimelineIcon />} label="Timeline" iconPosition="start" />
                        <Tab icon={<LeaderboardIcon />} label="Eliminations" iconPosition="start" />
                        <Tab icon={<SearchIcon />} label="Player Journey" iconPosition="start" />
                    </Tabs>
                </Box>

                {/* Tab Content */}
                <Box sx={{ flex: 1, overflow: 'hidden', px: 2 }}>
                    <TabPanel value={tabValue} index={0}>
                        <Grid container spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
                            <Grid item xs={12} md={6} sx={{ height: '100%', overflow: 'auto', ...THIN_SCROLLBAR_STYLES }}>
                                <AnimatePresence>
                                    {displayEvents.map((event, index) => (
                                        <EventCard
                                            key={event.type === 'grouped_moves' ? `batch-${event.batchId}` : `event-${(event as ITournamentEvent).seq}`}
                                            event={event}
                                            isSelected={selectedEventIndex === index}
                                            onClick={() => handleEventClick(index)}
                                        />
                                    ))}
                                </AnimatePresence>
                            </Grid>
                            <Grid item xs={12} md={6} sx={{ height: '100%', overflow: 'hidden' }}>
                                <EventDetail event={selectedEvent} />
                            </Grid>
                        </Grid>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <EliminationFeed progression={progression} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <PlayerSearch
                            progression={progression}
                            searchTerm={playerSearchTerm}
                            onSearchChange={setPlayerSearchTerm}
                            selectedPlayerId={selectedPlayerId}
                            onPlayerSelect={setSelectedPlayerId}
                        />
                    </TabPanel>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default TableStructureModal;
